// Translation system for NodeCast

export type Language = 'en' | 'es';

export interface Translations {
  // Navigation
  nav: {
    home: string;
    messages: string;
    settings: string;
  };

  // Settings
  settings: {
    title: string;
    general: string;
    appearance: string;
    backupKeys: string;
    backupDescription: string;
    downloadKeys: string;
    keysSaved: string;
    dismissBackup: string;
    language: string;
    selectLanguage: string;
    accountKeys: string;
    publicKey: string;
    copied: string;
    loginMethod: string;
  };

  // Auth
  auth: {
    login: string;
    signup: string;
    instantSignup: string;
    signupDescription: string;
    creatingAccount: string;
    generatingKey: string;
    newToNodeCast: string;
    newDescription: string;
    orLogin: string;
  };

  // Common
  common: {
    back: string;
    save: string;
    cancel: string;
    delete: string;
    edit: string;
    loading: string;
  };
}

const en: Translations = {
  nav: {
    home: 'Home',
    messages: 'Messages',
    settings: 'Settings',
  },
  settings: {
    title: 'Settings',
    general: 'General',
    appearance: 'Appearance',
    backupKeys: 'Backup Your Keys',
    backupDescription: 'Download your public and private keys for safekeeping. Store them securely.',
    downloadKeys: 'Download Keys',
    keysSaved: 'Keys downloaded successfully!',
    dismissBackup: 'Dismiss',
    language: 'Language',
    selectLanguage: 'Select your preferred language',
    accountKeys: 'Account Keys',
    publicKey: 'Public Key (npub)',
    copied: 'Copied!',
    loginMethod: 'Login method',
  },
  auth: {
    login: 'Log In',
    signup: 'Sign Up',
    instantSignup: 'Instant Signup',
    signupDescription: 'Click below to instantly create your account. Your secret key will be generated automatically and saved to your account settings for backup.',
    creatingAccount: 'Creating your account...',
    generatingKey: 'Generating your secure key',
    newToNodeCast: 'New to NodeCast?',
    newDescription: 'Create a new account to get started. It is free and open.',
    orLogin: 'Or log in',
  },
  common: {
    back: 'Back',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    loading: 'Loading...',
  },
};

const es: Translations = {
  nav: {
    home: 'Inicio',
    messages: 'Mensajes',
    settings: 'Configuración',
  },
  settings: {
    title: 'Configuración',
    general: 'General',
    appearance: 'Apariencia',
    backupKeys: 'Respalda tus Claves',
    backupDescription: 'Descarga tus claves públicas y privadas para guardarlas. Guárdalas de forma segura.',
    downloadKeys: 'Descargar Claves',
    keysSaved: '¡Claves descargadas exitosamente!',
    dismissBackup: 'Descartar',
    language: 'Idioma',
    selectLanguage: 'Selecciona tu idioma preferido',
    accountKeys: 'Claves de Cuenta',
    publicKey: 'Clave Pública (npub)',
    copied: '¡Copiado!',
    loginMethod: 'Método de inicio de sesión',
  },
  auth: {
    login: 'Iniciar Sesión',
    signup: 'Registrarse',
    instantSignup: 'Registro Instantáneo',
    signupDescription: 'Haz clic abajo para crear tu cuenta al instante. Tu clave secreta se generará automáticamente y se guardará en la configuración de tu cuenta para respaldo.',
    creatingAccount: 'Creando tu cuenta...',
    generatingKey: 'Generando tu clave segura',
    newToNodeCast: '¿Nuevo en NodeCast?',
    newDescription: 'Crea una cuenta nueva para comenzar. Es gratis y abierto.',
    orLogin: 'O iniciar sesión',
  },
  common: {
    back: 'Volver',
    save: 'Guardar',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    edit: 'Editar',
    loading: 'Cargando...',
  },
};

const translations: Record<Language, Translations> = {
  en,
  es,
};

export function getTranslations(lang: Language): Translations {
  return translations[lang] || translations.en;
}

export function getBrowserLanguage(): Language {
  const browserLang = navigator.language.split('-')[0];
  return (browserLang === 'es' ? 'es' : 'en') as Language;
}

export const SUPPORTED_LANGUAGES: { code: Language; name: string; nativeName: string }[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
];
